import { getUsersTenantImage } from "~/apiclient/sdk.gen"
import type { PinnedDeviceDto, UpsertUserSettingsRequest, UserSettings } from "~/apiclient/types.gen"

// Map a server-returned UserSettings to the request body shape used for upserts.
// Server-side metadata (id, createdAt, updatedAt, email) is dropped; PinnedDevice
// (optional fields) is narrowed to PinnedDeviceDto (required fields).
export const toUpsertUserSettings = (s: UserSettings | undefined): UpsertUserSettingsRequest => ({
	language: s?.language ?? null,
	pinnedFleets: s?.pinnedFleets ?? null,
	pinnedDevices:
		s?.pinnedDevices
			?.filter((d): d is { fleetId: string; devices: string[] } => d.fleetId != null && d.devices != null)
			.map((d): PinnedDeviceDto => ({ fleetId: d.fleetId, devices: d.devices })) ?? null,
	fleetsView: s?.fleetsView ?? null,
	notificationsTouched: s?.notificationsTouched ?? null
})

export const useGetUserSettings = defineQuery(() => {
	const browserLocale = navigator.language.split(/-|_/)[0]

	const { ...query } = useQuery({
		key: () => ["userSettings"],
		query: async () => {
			const { data, error } = await getUsersByBrowserLocaleSettings({
				path: {
					browserLocale: browserLocale ?? "en"
				}
			})

			if (error) {
				console.error("failed to get user settings: ", error)
				return undefined
			}
			return {
				fleetsView: data?.fleetsView ?? FleetsView.GRID,
				language: data?.language ?? AvailableLanguages.EN,
				pinnedFleets: data?.pinnedFleets ?? [],
				pinnedDevices: data?.pinnedDevices ?? [],
				notificationsTouched: data?.notificationsTouched ?? false
			} as UserSettings
		}
	})

	return { ...query }
})

export const useTenantImage = defineQuery(() => {
	const { ...query } = useQuery({
		key: () => ["tenantImage"],
		placeholderData: "",
		query: async () => {
			const { data, error } = await getUsersTenantImage({})

			if (error) {
				console.error("failed to get tenantImage: ", error)
				return ""
			}
			return data?.imageRelativeUrl ?? ""
		}
	})

	return { ...query }
})

export const useTenantImages = defineQuery(() => {
	const { ...query } = useQuery({
		key: () => ["tenantImages"],
		placeholderData: [] as string[],
		query: async () => {
			const { data, error } = await getUsersTenantImages({})

			if (error) {
				console.error("failed to get tenantImages: ", error)
				return [] as string[]
			}
			return data?.imageRelativeUrls ?? []
		}
	})

	return { ...query }
})
